import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function TermsPage() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/pt')) i18n.changeLanguage('pt');
    else if (path.startsWith('/de')) i18n.changeLanguage('de');
    else i18n.changeLanguage('en');
  }, [location.pathname, i18n]);

  const langCode = i18n.language || 'en';

  const content: Record<string, { title: string; h1: string; body: string }> = {
    en: {
      title: 'Terms of Use | CSSBuy Spreadsheet',
      h1: 'Terms of Use',
      body: `By accessing and using this website, you accept and agree to be bound by these Terms of Use.

Independent Resource: This website is an independent informational resource and is not affiliated with, endorsed by, or operated by CSSBuy. All information provided is for educational and research purposes only.

Use of Information: The content on this website is provided for general informational purposes. We do not guarantee the accuracy, completeness, or reliability of any information. Users should conduct their own research before making any decisions.

External Links: This website contains links to third-party websites. We are not responsible for the content, accuracy, or practices of these external sites. Visiting external links is at your own risk.

Intellectual Property: All content on this website, including text, images, and design, is protected by copyright. You may not reproduce, distribute, or create derivative works without permission.

Limitation of Liability: This website and its owners shall not be liable for any damages arising from the use of information provided on this site.

Changes to Terms: We reserve the right to modify these terms at any time. Continued use of the website constitutes acceptance of any changes.

Governing Law: These terms are governed by applicable laws. Any disputes shall be resolved in accordance with the relevant jurisdiction.`,
    },
    pt: {
      title: 'Termos de Uso | CSSBuy Spreadsheet',
      h1: 'Termos de Uso',
      body: `Ao acessar e usar este site, você aceita e concorda em se vincular a estes Termos de Uso.

Recurso Independente: Este site é um recurso de informação independente e não é afiliado, endossado ou operado pelo CSSBuy. Todas as informações fornecidas são apenas para fins educacionais e de pesquisa.

Uso das Informações: O conteúdo deste site é fornecido apenas para fins informativos gerais. Não garantimos a precisão, integridade ou confiabilidade de qualquer informação. Os usuários devem conduzir sua própria pesquisa antes de tomar qualquer decisão.

Links Externos: Este site contém links para sites de terceiros. Não somos responsáveis pelo conteúdo, precisão ou práticas desses sites externos. Visitar links externos é por sua própria conta e risco.

Propriedade Intelectual: Todo conteúdo deste site, incluindo texto, imagens e design, é protegido por direitos autorais. Você não pode reproduzir, distribuir ou criar obras derivadas sem permissão.

Limitação de Responsabilidade: Este site e seus proprietários não serão responsáveis por quaisquer danos decorrentes do uso de informações fornecidas neste site.

Alterações nos Termos: Reservamos o direito de modificar estes termos a qualquer momento. O uso continuado do site constitui aceitação de quaisquer alterações.

Legislação Aplicável: Estes termos são regidos pelas leis aplicáveis. Quaisquer disputas serão resolvidas de acordo com a jurisdição relevante.`,
    },
    de: {
      title: 'Nutzungsbedingungen | CSSBuy Spreadsheet',
      h1: 'Nutzungsbedingungen',
      body: `Durch den Zugriff auf und die Nutzung dieser Website akzeptieren Sie diese Nutzungsbedingungen und stimmen ihnen zu.

Unabhängige Ressource: Diese Website ist eine unabhängige Informationsressource und ist nicht mit CSSBuy verbunden, von CSSBuy unterstützt oder von CSSBuy betrieben. Alle bereitgestellten Informationen dienen ausschließlich Bildungs- und Recherchezwecken.

Nutzung der Informationen: Die Inhalte dieser Website werden nur zu allgemeinen Informationszwecken bereitgestellt. Wir garantieren nicht die Genauigkeit, Vollständigkeit oder Zuverlässigkeit von Informationen. Benutzer sollten ihre eigene Recherche durchführen, bevor sie Entscheidungen treffen.

Externe Links: Diese Website enthält Links zu Drittanbieter-Websites. Wir sind nicht für die Inhalte, Genauigkeit oder Praktiken dieser externen Seiten verantwortlich. Das Besuchen externer Links erfolgt auf eigenes Risiko.

Geistiges Eigentum: Alle Inhalte dieser Website, einschließlich Text, Bilder und Design, sind urheberrechtlich geschützt. Sie dürfen keine Reproduktionen, Verbreitungen oder abgeleiteten Werke ohne Erlaubnis erstellen.

Haftungsbeschränkung: Diese Website und ihre Eigentümer haften nicht für Schäden, die aus der Nutzung der auf dieser Website bereitgestellten Informationen entstehen.

Änderungen der Bedingungen: Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Die fortgesetzte Nutzung der Website stellt die Annahme von Änderungen dar.

Anwendbares Recht: Diese Bedingungen unterliegen dem anwendbaren Recht. Streitigkeiten werden gemäß der relevanten Gerichtsbarkeit beigelegt.`,
    },
  };

  const c = content[langCode] || content.en;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <header className="pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-8 bg-background-100 border-b border-background-200">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            {c.h1}
          </h1>
        </div>
      </header>

      <article className="py-12 md:py-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <nav className="mb-8 text-sm text-foreground-500">
            <Link to={`/${langCode}/`} className="hover:text-primary-600 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">Terms</span>
          </nav>

          <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8">
            {c.body.split('\n\n').map((paragraph, idx) => (
              <p key={idx} className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4 last:mb-0">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}